/**
 * useForgeViewer — encapsulates the the Panorix model service Viewer (LMV)
 * lifecycle for React.
 *
 *   - Lazy-loads the Viewer SDK from the APS CDN (cached across calls)
 *   - Initializes Autodesk.Viewing with a token endpoint that calls our backend
 *   - Returns the live `viewer` instance, the load state and a `loadModel(urn)` fn
 *
 * The viewer is mounted into a ref-bound <div>. Camera sync, overlay groups,
 * raycasting etc. are handled by the consuming components.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';

const FORGE_VERSION = '7.99';  // pinned — see https://aps.autodesk.com/en/docs/viewer/v7/release-notes/ for version table
const SDK_URL = `https://developer.api.autodesk.com/modelderivative/v2/viewers/${FORGE_VERSION}/viewer3D.min.js`;
const SDK_CSS = `https://developer.api.autodesk.com/modelderivative/v2/viewers/${FORGE_VERSION}/style.min.css`;

let _sdkPromise = null;
function _loadSdk() {
  if (_sdkPromise) return _sdkPromise;
  _sdkPromise = new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.Autodesk?.Viewing) {
      return resolve(window.Autodesk);
    }
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = SDK_CSS;
    document.head.appendChild(css);
    const s = document.createElement('script');
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => resolve(window.Autodesk);
    s.onerror = () => reject(new Error('Failed to load the 3D viewer SDK'));
    document.head.appendChild(s);
  });
  return _sdkPromise;
}

export function useForgeViewer({ projectId, urn = null, onCameraChange = null, onModelLoaded = null,
                                tokenUrl = null, extensions = null, region = null }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [status, setStatus] = useState('idle'); // idle | loading | sdk-ready | model-loading | ready | error
  const [error, setError] = useState(null);

  // 1) Load SDK + init viewer
  useEffect(() => {
    let mounted = true;
    setStatus('loading');
    _loadSdk()
      .then(async (Autodesk) => {
        if (!mounted) return;
        // The token endpoint is per-module because entitlement is per-URN: the EDM mint
        // answers for a document the caller can read, the scan3d mint for a project they
        // are a member of. One viewer, several gates — never one gate for everything.
        const tokenRes = await api.get(tokenUrl || `/v1/projects/${projectId}/scan3d/viewer/forge-token`);
        // Two mints, two response shapes: the scan3d mint answers `access_token`, the EDM
        // mint answers `token` with its scope justification alongside. Accept both rather
        // than making one module's field name the viewer's contract.
        const d = tokenRes.data || {};
        const token = d.access_token || d.token;
        const expires = d.expires_in_sec || d.expiresIn || 3600;
        if (!token) throw new Error('Forge token unavailable');

        // The derivative lives in a region and cannot be read through another one. An
        // EMEA model opened against derivativeV2 answers 404, which the viewer surfaces as
        // NETWORK_FILE_NOT_FOUND — indistinguishable from a corrupt file unless you know.
        const eu = ['EMEA', 'EU'].includes(String(region || '').toUpperCase());
        const options = {
          env: 'AutodeskProduction',
          api: eu ? 'derivativeV2_EU' : 'derivativeV2',
          getAccessToken: (cb) => cb(token, expires),
        };

        await new Promise((res) => Autodesk.Viewing.Initializer(options, res));
        if (!mounted) return;
        if (!containerRef.current) return;
        const v = new Autodesk.Viewing.GuiViewer3D(containerRef.current, {
          extensions: extensions || ['Autodesk.DocumentBrowser'],
        });
        const start = v.start();
        if (start > 0) throw new Error(`Forge viewer.start() failed: ${start}`);
        viewerRef.current = v;

        // Camera-change pipe
        if (onCameraChange) {
          v.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, () => {
            onCameraChange(v);
          });
        }
        setStatus('sdk-ready');
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e.message || String(e));
        setStatus('error');
      });

    return () => {
      mounted = false;
      try {
        if (viewerRef.current) {
          viewerRef.current.finish();
          viewerRef.current = null;
        }
      } catch (_) { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, tokenUrl, region]);

  // 2) Load a model when urn changes
  const loadModel = useCallback((urnToLoad) => {
    if (!urnToLoad || !viewerRef.current || !window.Autodesk?.Viewing?.Document) return;
    setStatus('model-loading');
    const docUrn = urnToLoad.startsWith('urn:') ? urnToLoad : `urn:${urnToLoad}`;
    window.Autodesk.Viewing.Document.load(
      docUrn,
      (doc) => {
        const default3d = doc.getRoot().getDefaultGeometry();
        viewerRef.current.loadDocumentNode(doc, default3d).then(() => {
          setStatus('ready');
          if (onModelLoaded) onModelLoaded(viewerRef.current);
        });
      },
      (errCode) => {
        setError(`Model load failed (code ${errCode})`);
        setStatus('error');
      }
    );
  }, [onModelLoaded]);

  useEffect(() => {
    if (urn && status === 'sdk-ready') {
      loadModel(urn);
    }
  }, [urn, status, loadModel]);

  return { containerRef, viewer: viewerRef.current, status, error, loadModel };
}
